// Create OverlayView class factory to avoid immediate reference to window.google

export const createOverlayViewClass = () => {
  if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
    return null;
  }

  class OverlayView extends window.google.maps.OverlayView {
    content = null;

    bounds = null;

    lastTransformX = null;

    lastTransformY = null;

    lastWidth = null;

    lastHeight = null;

    constructor(props) {
      super(props);
      this.content = props.content;
      this.bounds = props.overlayBounds;
    }

    /** Called when the popup is added to the map. */
    onAdd = () => {
      this.getPanes().floatPane.appendChild(this.content);
    };

    /** Called when the popup is removed from the map. */
    onRemove = () => {
      if (this.content.parentElement) {
        this.content.parentElement.removeChild(this.content);
      }
    };

    /** Called each frame when the popup needs to draw itself. */
    draw = () => {
      if (this.bounds) {
      // We use the south-west and north-east
      // coordinates of the overlay to peg it to the correct position and size.
      // To do this, we need to retrieve the projection from the overlay.
        const overlayProjection = this.getProjection();

        if (overlayProjection) {
        // Retrieve the south-west and north-east coordinates of this overlay
        // in LatLngs and convert them to pixel coordinates.
        // We'll use these coordinates to resize the div.
          const sw = overlayProjection.fromLatLngToDivPixel(
            this.bounds.getSouthWest(),
          );
          const ne = overlayProjection.fromLatLngToDivPixel(
            this.bounds.getNorthEast(),
          );

          const transformX = sw.x;
          const transformY = ne.y;
          const rawWidth = ne.x - sw.x;
          const rawHeight = sw.y - ne.y;
          const width = `${rawWidth}px`;
          const height = `${rawHeight}px`;

          // Only update DOM if values actually changed
          // This prevents layout thrashing from unnecessary style updates

          // Use CSS transform instead of left/top for GPU acceleration
          // transform is composited on GPU, avoiding layout/paint on CPU
          if (this.lastTransformX !== transformX || this.lastTransformY !== transformY) {
            this.content.style.transform = `translate(${transformX}px, ${transformY}px) translateZ(0)`;
            this.lastTransformX = transformX;
            this.lastTransformY = transformY;
          }

          if (rawWidth !== this.lastWidth) {
            this.content.style.width = width;
            this.lastWidth = rawWidth;
          }
          if (rawHeight !== this.lastHeight) {
            this.content.style.height = height;
            this.lastHeight = rawHeight;
          }
        }
      }
    };
  }

  return OverlayView;
};

export function createOverlayElement() {
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.display = 'inline-block';
  el.style.width = '9999px';

  // Performance optimizations for smooth panning
  // Use GPU-accelerated compositing
  el.style.willChange = 'transform';
  el.style.transform = 'translateZ(0)'; // Force GPU layer

  return el;
}

// Get longitude of Google maps tile.
export function tile2lng(x, z) {
  return (x / 2 ** z) * 360 - 180;
}

// Get latitude of Google maps tile.
export function tile2lat(y, z) {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

export function getOverlayBounds(overlaysInfo) {
  // Check if Google Maps API is fully loaded
  if (!window.google || !window.google.maps || !window.google.maps.LatLng) {
    return null;
  }

  // Calcluate top right corner longitude and latitude of overlay bounds. Since
  // the tile2 functions give us the top left coords of a tile, we actually have
  // to ues the tile to the right of the top right tile, hence the + 1.
  const neTileX = parseInt(overlaysInfo.topRightX, 10) + 1;
  const neLng = tile2lng(neTileX, overlaysInfo.zoom);
  const neLat = tile2lat(overlaysInfo.topRightY, overlaysInfo.zoom);

  // Calcluate bottom left corner longitude and latitude of overlay bounds.
  // Since the tile2 functions give us the top left coords of a tile, we
  // actually have to ues the tile below the the bottom left tile, hence the - 1.
  const swTileY = parseInt(overlaysInfo.bottomLeftY, 10) + 1;
  const swTileX = parseInt(overlaysInfo.bottomLeftX, 10);
  const swLng = tile2lng(swTileX, overlaysInfo.zoom);
  const swLat = tile2lat(swTileY, overlaysInfo.zoom);

  const bounds = new window.google.maps.LatLngBounds(
    new window.google.maps.LatLng(swLat, swLng),
    new window.google.maps.LatLng(neLat, neLng),
  );

  return bounds;
}
